import { useState } from "react";
import { useAxios } from "../../../utils/useAxios";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { ERROR_MESSAGES } from "../../../constant/constant";

/**
 * @typedef {Object} ReassignClassroomTeacherParams
 * @property {number|string} classroomId - The classroom ID to update
 * @property {number} teacherId - The new teacher's ID
 */

/**
 * Custom hook to reassign a classroom to a different teacher.
 *
 * @returns {{ reassignTeacher, loading, error, success, reset }}
 */
const useReassignClassroomTeacher = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const { axiosInstance } = useAxios();

  /** Resets state back to initial (useful before re-opening the modal) */
  const reset = () => {
    setLoading(false);
    setError("");
    setSuccess(false);
  };

  /**
   * Reassigns the classroom to a new teacher.
   * @param {ReassignClassroomTeacherParams} params
   * @returns {Promise<boolean>} true on success, false on failure
   */
  const reassignTeacher = async ({ classroomId, teacherId }) => {
    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      await axiosInstance.post(
        `${TENANT_DOMAIN}/classroom/${classroomId}/reassign-teacher`,
        { teacher_id: teacherId },
      );

      setSuccess(true);
      return true;
    } catch (err) {
      let message = "";

      if (err?.response?.data?.errors) {
        message = Object.values(err.response.data.errors)[0][0];
      }

      if (err?.response?.status === 500) {
        setError(ERROR_MESSAGES.INTERNAL_SERVER);
      } else {
        setError(
          message || err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN,
        );
      }

      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    reassignTeacher,
    loading,
    error,
    success,
    reset,
  };
};

export default useReassignClassroomTeacher;
